import { motion } from 'framer-motion';
import {
  Wrench,
  Cpu,
  Truck,
  Shield,
  Tag,
  Percent,
  Package,
  CircleDot,
} from 'lucide-react';
import { useProposalData } from '../../context';
import { Section, SectionHeader } from '../layout';
import { formatCurrency } from '../../utils';
import type { AddonType } from '../../types';

const addonIcons: Record<AddonType, typeof Wrench> = {
  Attachment: Wrench,
  Technology: Cpu,
  'Freight-In': Truck,
  'Freight-Out': Truck,
  Installation: Package,
  Warranty: Shield,
  Discount: Tag,
  Rebate: Percent,
  Other: CircleDot,
};

const addonColors: Record<AddonType, string> = {
  Attachment: 'bg-blue-100 text-blue-700',
  Technology: 'bg-purple-100 text-purple-700',
  'Freight-In': 'bg-amber-100 text-amber-700',
  'Freight-Out': 'bg-amber-100 text-amber-700',
  Installation: 'bg-teal-100 text-teal-700',
  Warranty: 'bg-indigo-100 text-indigo-700',
  Discount: 'bg-green-100 text-green-700',
  Rebate: 'bg-green-100 text-green-700',
  Other: 'bg-gray-100 text-gray-700',
};

export function AddonsSection() {
  const proposal = useProposalData();

  if (!proposal.sections.showAddons || proposal.addons.length === 0) {
    return null;
  }

  // Separate credits from charges
  const charges = proposal.addons.filter(addon => !addon.isCredit);
  const credits = proposal.addons.filter(addon => addon.isCredit);

  const chargesTotal = charges.reduce((sum, addon) => sum + addon.extended, 0);
  // Credits come in as negative values, so use Math.abs to get the positive amount
  const creditsTotal = credits.reduce((sum, addon) => sum + Math.abs(addon.extended), 0);
  const netTotal = chargesTotal - creditsTotal;

  return (
    <Section id="addons">
      <SectionHeader
        title="ADD-ONS & ACCESSORIES"
        subtitle="Additional Items"
      />

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Table */}
        <div className="lg:col-span-2">
          <motion.div
            className="bg-white rounded-xl shadow-lg overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Charges */}
            {charges.length > 0 && (
              <div>
                <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                  <h3 className="font-heading text-sm uppercase tracking-wider text-gray-600">
                    Add-ons & Services
                  </h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {charges.map((addon, index) => {
                    const Icon = addonIcons[addon.type];
                    return (
                      <motion.div
                        key={addon.lineNumber}
                        className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05 }}
                      >
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${addonColors[addon.type]}`}>
                          <Icon className="w-5 h-5" />
                        </div>

                        {/* Description */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-hul-navy">
                              {addon.description}
                            </span>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${addonColors[addon.type]}`}>
                              {addon.type}
                            </span>
                          </div>
                          {addon.notes && (
                            <p className="text-sm text-gray-500 mt-1">{addon.notes}</p>
                          )}
                        </div>

                        {/* Quantity */}
                        {addon.quantity > 1 && (
                          <div className="text-sm text-gray-500">
                            {addon.quantity} × {formatCurrency(addon.unitPrice)}
                          </div>
                        )}

                        {/* Price */}
                        <div className="font-mono font-medium text-hul-navy">
                          {formatCurrency(addon.extended)}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Credits */}
            {credits.length > 0 && (
              <div>
                <div className="bg-green-50 px-6 py-3 border-b border-green-100">
                  <h3 className="font-heading text-sm uppercase tracking-wider text-green-700">
                    Discounts & Rebates
                  </h3>
                </div>
                <div className="divide-y divide-green-50">
                  {credits.map((addon, index) => {
                    const Icon = addonIcons[addon.type];
                    return (
                      <motion.div
                        key={addon.lineNumber}
                        className="flex items-center gap-4 px-6 py-4 bg-green-50/50 hover:bg-green-50 transition-colors"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05 }}
                      >
                        {/* Icon */}
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-100 text-green-700">
                          <Icon className="w-5 h-5" />
                        </div>

                        {/* Description */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-green-700">
                              {addon.description}
                            </span>
                            <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
                              {addon.type}
                            </span>
                          </div>
                          {addon.notes && (
                            <p className="text-sm text-green-600 mt-1">{addon.notes}</p>
                          )}
                        </div>

                        {/* Quantity */}
                        {addon.quantity > 1 && (
                          <div className="text-sm text-green-600">
                            {addon.quantity} × {formatCurrency(addon.unitPrice)}
                          </div>
                        )}

                        {/* Price (Credit) */}
                        <div className="font-mono font-medium text-green-700">
                          -{formatCurrency(Math.abs(addon.extended))}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Summary Card */}
        <div className="lg:col-span-1">
          <motion.div
            className="bg-hul-navy text-white rounded-xl p-6 sticky top-24"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="font-heading text-lg uppercase tracking-wider mb-6 flex items-center gap-3">
              <span className="w-8 h-0.5 bg-hul-red" />
              Add-ons Summary
            </h3>

            <div className="space-y-4">
              {charges.length > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-white/70">Add-ons ({charges.length})</span>
                  <span className="font-mono">{formatCurrency(chargesTotal)}</span>
                </div>
              )}

              {credits.length > 0 && (
                <div className="flex justify-between items-center text-green-400">
                  <span>Savings ({credits.length})</span>
                  <span className="font-mono">-{formatCurrency(creditsTotal)}</span>
                </div>
              )}

              <div className="h-px bg-white/20 my-4" />

              <div className="flex justify-between items-center">
                <span className="font-heading text-lg">Net Add-ons</span>
                <span className="font-mono text-2xl font-bold text-white">
                  {formatCurrency(netTotal)}
                </span>
              </div>
            </div>

            {credits.length > 0 && (
              <div className="mt-6 p-4 bg-green-500/20 rounded-lg">
                <div className="flex items-center gap-2 text-green-300 text-sm">
                  <Tag className="w-4 h-4" />
                  <span>
                    You're saving{' '}
                    <span className="font-mono font-bold">
                      {formatCurrency(creditsTotal)}
                    </span>{' '}
                    on this proposal!
                  </span>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </Section>
  );
}
